/**
 * ProfilesPage - Permission Profile Management
 *
 * Allows organisation owners to:
 * - View all profiles in a paginated table
 * - Create new profiles with selected permissions
 * - Edit profiles (double-click on row)
 * - Delete profiles (if not assigned to any users)
 */

import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchProfiles,
    fetchPermissionsGrouped,
    openCreateModal,
    openEditModal,
    openDeleteModal,
    closeDeleteModal,
    deleteProfile,
    setFilters,
    setPage,
    clearOperationSuccess,
    selectProfiles,
    selectIsLoading,
    selectError,
    selectPagination,
    selectFilters,
    selectIsCreateModalOpen,
    selectIsEditModalOpen,
    selectIsDeleteModalOpen,
    selectProfileToDelete,
    selectIsDeleting,
    selectIsRefreshing,
    selectOperationError,
    selectOperationSuccess,
} from '../store/slices/profileSlice';
import MainLayout from './Layout.jsx';
import ProfileTable from './profiles/ProfileTable';
import ProfileModal from './profiles/ProfileModal';
import DeleteConfirmModal from './common/DeleteConfirmModal';
import Toast from './common/Toast';

const ProfilesPage = () => {
    const dispatch = useDispatch();

    // Selectors
    const profiles = useSelector(selectProfiles);
    const isLoading = useSelector(selectIsLoading);
    const error = useSelector(selectError);
    const pagination = useSelector(selectPagination);
    const filters = useSelector(selectFilters);
    const isCreateModalOpen = useSelector(selectIsCreateModalOpen);
    const isEditModalOpen = useSelector(selectIsEditModalOpen);
    const isDeleteModalOpen = useSelector(selectIsDeleteModalOpen);
    const profileToDelete = useSelector(selectProfileToDelete);
    const isDeleting = useSelector(selectIsDeleting);
    const isRefreshing = useSelector(selectIsRefreshing);
    const operationError = useSelector(selectOperationError);
    const operationSuccess = useSelector(selectOperationSuccess);

    // Fetch profiles on mount and when filters/pagination change
    useEffect(() => {
        dispatch(fetchProfiles({
            page: pagination.page,
            size: pagination.size,
            activeOnly: filters.activeOnly,
            search: filters.search,
        }));
    }, [dispatch, pagination.page, pagination.size, filters.activeOnly, filters.search]);

    // Fetch permissions on mount (for modal)
    useEffect(() => {
        dispatch(fetchPermissionsGrouped());
    }, [dispatch]);

    // Clear success message after delay and refetch data
    useEffect(() => {
        if (operationSuccess) {
            // Refetch profiles immediately after successful operation
            dispatch(fetchProfiles({
                page: pagination.page,
                size: pagination.size,
                activeOnly: filters.activeOnly,
                search: filters.search,
            }));

            // Clear success message after delay
            const timer = setTimeout(() => {
                dispatch(clearOperationSuccess());
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [operationSuccess, dispatch, pagination.page, pagination.size, filters.activeOnly, filters.search]);

    // Handlers
    const handleCreateClick = useCallback(() => {
        dispatch(openCreateModal());
    }, [dispatch]);

    const handleRowDoubleClick = useCallback((profile) => {
        dispatch(openEditModal(profile));
    }, [dispatch]);

    const handleDeleteClick = useCallback((profile) => {
        dispatch(openDeleteModal(profile));
    }, [dispatch]);

    const handleConfirmDelete = useCallback(() => {
        if (profileToDelete) {
            dispatch(deleteProfile(profileToDelete.uuid));
        }
    }, [dispatch, profileToDelete]);

    const handleCancelDelete = useCallback(() => {
        dispatch(closeDeleteModal());
    }, [dispatch]);

    const handleSearchChange = useCallback((search) => {
        dispatch(setFilters({ search }));
    }, [dispatch]);

    const handleActiveOnlyChange = useCallback((activeOnly) => {
        dispatch(setFilters({ activeOnly }));
    }, [dispatch]);

    const handlePageChange = useCallback((page) => {
        dispatch(setPage(page));
    }, [dispatch]);

    return (
        <MainLayout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-display font-bold text-gray-900">
                        Permission Profiles
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Create and manage permission profiles for your team members.
                    </p>
                </div>

                {/* Actions Bar */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Search */}
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search profiles..."
                                value={filters.search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                            <svg
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>

                        {/* Active Only Toggle */}
                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={filters.activeOnly}
                                onChange={(e) => handleActiveOnlyChange(e.target.checked)}
                                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                            Active only
                        </label>
                    </div>

                    {/* Create Button */}
                    <button
                        onClick={handleCreateClick}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Profile
                    </button>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                        {error}
                    </div>
                )}

                {/* Profile Table */}
                <ProfileTable
                    profiles={profiles}
                    isLoading={isLoading}
                    isRefreshing={isRefreshing}
                    pagination={pagination}
                    onRowDoubleClick={handleRowDoubleClick}
                    onDeleteClick={handleDeleteClick}
                    onPageChange={handlePageChange}
                />

                {/* Create/Edit Modal */}
                {(isCreateModalOpen || isEditModalOpen) && (
                    <ProfileModal />
                )}

                {/* Delete Confirmation Modal */}
                {isDeleteModalOpen && profileToDelete && (
                    <DeleteConfirmModal
                        title="Delete Profile"
                        message={
                            profileToDelete.userCount > 0
                                ? `Cannot delete "${profileToDelete.name}" because it is assigned to ${profileToDelete.userCount} user(s). Please reassign those users first.`
                                : `Are you sure you want to delete the profile "${profileToDelete.name}"? This action cannot be undone.`
                        }
                        confirmLabel="Delete"
                        isDestructive
                        isLoading={isDeleting}
                        canConfirm={profileToDelete.userCount === 0}
                        error={operationError}
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                    />
                )}

                {/* Success Toast */}
                {operationSuccess && (
                    <Toast
                        message={operationSuccess}
                        type="success"
                        onClose={() => dispatch(clearOperationSuccess())}
                    />
                )}
            </div>
        </MainLayout>
    );
};

export default ProfilesPage;